<template>
  <div ref="root">
    <div
      v-show="!editMode"
      ref="static"
      v-touch="onTouch"
      class="editor read-only"
      tabindex="0"
      @dblclick.stop="onDblClick"
      @mousedown="onMouseDown"
      @click="click($event.target)"
    >
      <div ref="wrapper" class="editor">
        <div
          ref="staticContent"
          class="ProseMirror"
          v-html="contentPlaceholder"
        />
      </div>
    </div>
    <RichTextInput
      v-if="editMode"
      ref="input"
      :content="currentContent"
      :variable-names="() => varNames"
      :extension-options="extensionOptions"
      :caret="caret"
      @focus="emit('focus')"
      @blur="onInputBlur"
      @update="onInputUpdate"
      @submit="emit('submit')"
    />
    <RichTextValidator
      v-if="extensionOptions.codeBlockHighlight"
      :content="currentContent"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUpdated, ref, watch } from 'vue';
import hljs from 'highlight.js';
import { isEmpty } from '@/utils.js';
import { useStore } from 'vuex';
import arcscript from '@/arcscript/arcscript.js';
import emitter from 'tiny-emitter/instance';
import { EVENT, PANZOOM } from '@/constants.js';
import RichTextInput from './RichTextInput.vue';
import RichTextValidator from './RichTextValidator.vue';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  editable: {
    type: Boolean,
    default: true,
  },
  activateOnDoubleClick: {
    type: Boolean,
    default: false,
  },
  selectOnDoubleClick: {
    type: Boolean,
    default: true,
  },
  extensionOptions: {
    type: Object,
    default: () => {
      return {
        bold: false,
        blockquote: false,
        codeBlockHighlight: null,
        customLink: false,
        customMention: false,
        dropEventHandler: false,
        hardbreak: false,
        history: false,
        horizontalRule: false,
        italic: false,
        placeholder: null,
        underline: false,
        toolbarOnSelect: false,
      };
    },
  },
});

const emit = defineEmits(['focus', 'blur', 'update', 'submit']);

const root = ref(null);
const input = ref(null);
const wrapper = ref(null);
const editMode = ref(false);
const content = ref(props.content);
const currentContent = computed({
  get() {
    return input.value ? input.value.getContent() : content.value;
  },
  set(value) {
    content.value = value;
  },
});

const contentPlaceholder = computed(() => {
  if (!isEmpty(currentContent.value)) {
    return currentContent.value;
  }
  const text = props.extensionOptions.placeholder?.text || '';
  return `<p class='is-editor-empty' data-placeholder='${text}'></p>`;
});
const store = useStore();
const varNames = computed(
  () => store.getters['project/data/variables/getNames']
);

const caret = ref(null);

if (props.extensionOptions.codeBlockHighlight) {
  hljs.registerLanguage(
    'arcscript',
    arcscript(() => varNames.value)
  );
}

const staticContent = ref(null);

/**
 * Applies the code highlighting to any code blocks in the static content
 */
function applyCodeHighlight() {
  if (!editMode.value && props.extensionOptions.codeBlockHighlight) {
    const nodes = staticContent.value.querySelectorAll('code');
    nodes.forEach(node => {
      const clonedNode = node.cloneNode(true);
      clonedNode.classList.add('language-arcscript');
      hljs.highlightElement(clonedNode);
      node.outerHTML = clonedNode.outerHTML;
    });
  }
}

/**
 * Set the content of the editor. If null clear the content
 * @param {String|null} value
 */
function setContent(value) {
  currentContent.value = value;
}

watch(varNames, () => {
  if (props.extensionOptions.codeBlockHighlight) {
    hljs.registerLanguage(
      'arcscript',
      arcscript(() => varNames.value)
    );
    hljs.configure({ languages: ['arcscript'] });
    applyCodeHighlight();
  }
});

watch(
  () => props.content,
  newValue => {
    if (newValue !== currentContent.value) {
      setContent(newValue || '');
    }
  }
);

onMounted(() => {
  applyCodeHighlight();
});

onUpdated(() => {
  applyCodeHighlight();
});

/**
 * Perform the click or tap on the RichTextEditor (read-only mode).
 * @param {Element} element The target element (the child of RichTextEditor that was clicked)
 */
function click(element) {
  if (
    !editMode.value &&
    (element.classList.contains('mention') ||
      element.classList.contains('hljs-mention'))
  ) {
    const { type, id } = element.dataset;
    if (type === 'component') {
      store.dispatch('project/config/components/setOpen', { id, apply: true });
    } else if (type === 'board') {
      store.dispatch('project/config/boards/setOpen', { id, apply: true });
    } else if (type === 'element') {
      const el = store.getters['project/data/elements/getNode'](id);
      emitter.emit(EVENT.ZOOMTONODE, {
        node: el,
        type: 'elements',
        boardId: store.getters['project/data/elements/getContainerNode'](
          id,
          'boards',
          'elements'
        ).id,
      });
    }
  }
}

/**
 * Perform the double click or double tap on the RichTextEditor (read-only mode)
 * @param {Number} x  The client x coordinate
 * @param {Number} y  The client y coordinate
 */
function dblClick(x, y) {
  if (!props.editable && props.selectOnDoubleClick) {
    const container = staticContent.value;
    if (window.getSelection) {
      const range = document.createRange();
      range.selectNodeContents(container);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    }
  }
  if (props.activateOnDoubleClick && props.editable && !editMode.value) {
    if (
      store.getters['project/config/getCursorMode'] !==
      PANZOOM.CURSOR_MODE.SELECT
    ) {
      store.dispatch('project/config/setCursorMode', {
        value: PANZOOM.CURSOR_MODE.SELECT,
        apply: true,
      });
    }
    caret.value = {
      mouse: { x, y },
      scrollTop: wrapper.value.scrollTop,
    };
    editMode.value = true;
  }
}

// RichTextInput handlers
/**
 * Event handler for custom rich text input blur event
 * @param {String} content
 */
function onInputBlur(text) {
  currentContent.value = text;
  if (editMode.value) {
    editMode.value = false;
    emit('blur', text);
  }
}
/**
 * Event handler for custom rich text input update event.
 * @param {String} content
 */
function onInputUpdate(text) {
  if (currentContent.value !== text) {
    currentContent.value = text;
    emit('update', text);
  }
}

// Static Editor handlers
/**
 * Event listener: double click on read-only mode
 * Starts editing if applicable (activateOnDoubleClick)
 * Event handler for hammer.js touch events. We are handling only 'press' event for pointer type 'touch'.
 * Pointer types are: 'touch', 'mouse', 'pen' or 'kinect'.
 * Type holds the name of the event: e.g. 'panstart', 'press' e.t.c.
 * SrcEvent	holds the source event: TouchEvent, MouseEvent or PointerEvent
 * @param {Object}  event  The hammer.js event
 */
function onTouch(event) {
  const { type, target, pointerType, srcEvent } = event;
  // Ignore any mouse events (these are handled by mouse event handlers)
  if (pointerType === 'mouse') {
    // Do not allow the event to reach ancestor elements
    event.stopPropagation();
    return;
  }

  if (type === 'singletap') {
    click(target);
    // Allow the event to reach ancestor elements (allow selection)
    return;
  }

  // Handle the double tap as a double click
  if (type === 'doubletap') {
    const { clientX, clientY } = srcEvent;
    dblClick(clientX, clientY);
    // Do not allow the event to reach ancestor elements
    event.stopPropagation();
    return;
  }

  // Handle the press event only to prevent context menu when on scrollbar
  if (type === 'press') {
    // Check for scrollbar press
    if (srcEvent.offsetX > staticContent.value.clientWidth) {
      event.stopPropagation(); // prevent contextmenu from activating
    }
  }
}

/**
 * Event listener: double click on read-only mode
 * Starts editing if applicable (activateOnDoubleClick)
 * @param {MouseEvent} event
 */
function onDblClick(event) {
  const { clientX, clientY, metaKey, ctrlKey, shiftKey } = event;
  // Do nothing if user has shift, ctrl or meta pressed
  if (metaKey || ctrlKey || shiftKey) return;
  dblClick(clientX, clientY);
}

/**
 * Event handler: mouse down on read-only mode.
 * Starts editing if applicable (no activateOnDoubleClick)
 * @param {MouseEvent} event
 */
function onMouseDown(event) {
  const { target, button, clientX, clientY } = event;
  // Disregard mention mouse down
  if (
    !editMode.value &&
    (target.classList.contains('mention') ||
      target.classList.contains('hljs-mention'))
  ) {
    return;
  }
  // Disregard link clicks
  if (target.tagName.toLowerCase() === 'a') return;
  // On middle and right click do not give focus to the editor
  if (button !== 0) {
    event.preventDefault();
    return;
  }

  // Start editing on mouse down
  if (!props.activateOnDoubleClick && props.editable && !editMode.value) {
    caret.value = { mouse: { x: clientX, y: clientY } };
    editMode.value = true;
  }
}

/**
 * Start the editing sequence on the editor
 * @param {String}  select  One of 'start', 'end', 'all' to determine the selection
 */
function startEditing(select = 'end') {
  if (!editMode.value) {
    caret.value = { select };
    editMode.value = true;
  }
}

/**
 * Returns whether the editor has no content
 * @returns {Boolean}
 */
function isContentEmpty() {
  if (editMode.value) {
    return input.value.isContentEmpty();
  }
  return (
    root.value.querySelectorAll('.ProseMirror > p.is-editor-empty:first-child')
      .length > 0
  );
}

/**
 * Blur the editor without sending blur event
 */
function blur() {
  if (input.value) {
    input.value.setContent(null); // Needed to clear the currentContent computed value
    input.value.removeToolbar();
  }
  editMode.value = false;
}

defineExpose({
  isEditMode: () => editMode.value,
  getContent: () => currentContent.value,
  setContent,
  isContentEmpty,
  startEditing,
  blur,
});
</script>

<style lang="scss">
.menubar {
  &.is-hidden {
    display: none;
  }
}
.editor .mention,
.editor a,
.editor a:visited {
  color: #db841e;
  display: inline-block;
  white-space: nowrap;
  cursor: pointer;
  user-select: text;

  &:hover {
    text-decoration: underline;
  }
}

.editor hr {
  margin: 17px 0 15px 0;
  background: #fff;
  opacity: 0.2;
  border: none;
  height: 2px;
}

.editor p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: hsla(0, 0%, 100%, 0.3);
  pointer-events: none;
  height: 0;
  white-space: normal;
  text-overflow: ellipsis;
}
.prototype-content .editor .editor {
  color: #bcbcbc;
  line-height: 1.5;
  font-size: 20px;
  padding: 20px 0 40px 0;
}

.prototype-content .editor:has(p.is-editor-empty) {
  padding: 0 0 20px 0;
}

.editor {
  flex: 1;
  height: 100%;
  &.read-only {
    user-select: text !important;
    overflow-y: auto;
  }

  .editor {
    font-size: 14px;
    line-height: 1.5;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 22px;
    flex: 1;
    user-select: auto;

    .ProseMirror {
      flex: 1;
      height: 100%;
    }
  }

  p {
    min-height: 21px; // Due to prosemirror rendering <br> on empty paragraph
    margin: 15px 0 0 0;

    &:first-child {
      margin: 0;
    }
  }

  pre {
    margin: 15px 0 0 0;
    &:first-child {
      margin: 0;
    }
  }
}

.editor .ProseMirror-focused {
  cursor: text !important;
  a {
    cursor: text !important;
    text-decoration: none;
  }
}

.editor a,
.editor a:visited {
  color: #db841e;
  background-position: 1px 5px;
  background-repeat: no-repeat;
  padding-left: 18px;
  background-size: 12px;
  background-image: linear-gradient(transparent, transparent),
    url(/images/external-link_opt.svg);
  text-decoration: none;
}

.title a {
  background-size: 14px;
  background-position: 1px 4px;
  padding-left: 20px !important;
}

.mention.mention-board {
  background-position: left 5px;
  background-repeat: no-repeat;
  padding-left: 18px;
  background-size: 14px;
  background-image: linear-gradient(transparent, transparent),
    url(/images/board_opt.svg);
  text-decoration: none;
  white-space: nowrap;
}

.title .mention.mention-board {
  background-size: 16px;
  background-position: 1px 4px;
  padding-left: 22px !important;
}

// SCROLLBARS

.editor {
  scrollbar-color: #242829 transparent;
  scrollbar-width: thin;
}

.editor::-webkit-scrollbar-track {
  background-color: transparent;
  border: none;
}

.editor::-webkit-scrollbar {
  width: 15px;
  background-color: transparent;
  // background-color:red;
}

.editor::-webkit-scrollbar-thumb {
  width: 10px;
  border-left: 5px solid rgba(0, 0, 0, 0);
  background-color: #242829;
  background-clip: padding-box;
}

.editor::-webkit-scrollbar-thumb:hover {
  background-color: #424a4c;
}

// Code highlight
@import '../RichTextCodeStyles.scss';
</style>
